import config from '../config/environment';
import supabase from '../config/supabase';
import type {
  ClintContactPayload,
  ClintCreateContactResult,
  ClintCreateDealResult,
  ClintDealPayload,
  ClintGetContactResult,
  ClintPushCintaNaturalStudentResult,
  ClintSyncLeadResult,
  ClintSyncProjectStudentResult,
} from '../types/clint';
import { handleServiceError } from '../utils/errorHandler';

type ClintAction =
  | 'create-contact'
  | 'get-contact'
  | 'sync-lead'
  | 'create-deal'
  | 'push-cinta-natural-student'
  | 'sync-project-student';

interface ClintSuccessBody<T> {
  success?: boolean;
  data?: T;
  error?: { code?: string; message?: string };
  requestId?: string;
}

class ClintServiceClass {
  private async getAuthHeaders(): Promise<Record<string, string>> {
    const { data: sessionData } = await supabase.auth.getSession();
    const token = sessionData?.session?.access_token;

    if (!token) {
      handleServiceError(new Error('Sessão expirada. Faça login novamente.'), 'autenticar na Clint');
    }

    return {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      apikey: config.supabaseAnonKey,
    };
  }

  private async invoke<T>(
    action: ClintAction,
    payload: Record<string, unknown> = {},
    path?: string,
  ): Promise<T> {
    const headers = await this.getAuthHeaders();
    const url = path
      ? `${config.supabaseFunctionsUrl}/clint/${path.replace(/^\/+/, '')}`
      : `${config.supabaseFunctionsUrl}/clint`;

    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(path ? payload : { action, payload }),
    });

    const body = await response.json().catch(() => ({})) as ClintSuccessBody<T>;

    if (!response.ok || body.success === false || body.data === undefined) {
      handleServiceError(
        new Error(body.error?.message || `Erro na integração Clint (${action})`),
        'integrar com a Clint',
      );
    }

    return body.data;
  }

  /** Cria um contato direto na Clint (POST /clint/contacts). */
  createContact(payload: ClintContactPayload): Promise<ClintCreateContactResult> {
    return this.invoke<ClintCreateContactResult>('create-contact', { ...payload }, 'contacts');
  }

  /** Busca um contato na Clint (action get-contact). */
  getContact(contactId: string): Promise<ClintGetContactResult> {
    return this.invoke<ClintGetContactResult>('get-contact', { contactId });
  }

  /**
   * Sincroniza uma linha de student_lead_qualifications com a Clint.
   * Normalmente disparado pela trigger; use para retry manual.
   */
  syncLead(leadQualificationId: number, options?: { force?: boolean }): Promise<ClintSyncLeadResult> {
    return this.invoke<ClintSyncLeadResult>(
      'sync-lead',
      {
        leadQualificationId,
        force: options?.force === true,
      },
      'leads/sync',
    );
  }

  /** Cria um deal na Clint (POST /clint/deals). */
  createDeal(payload: ClintDealPayload): Promise<ClintCreateDealResult> {
    return this.invoke<ClintCreateDealResult>('create-deal', { ...payload }, 'deals');
  }

  /** Envia uma aluna Cinta Natural para o quadro FUP CN, na etapa 1. */
  pushCintaNaturalStudent(studentId: number): Promise<ClintPushCintaNaturalStudentResult> {
    return this.invoke<ClintPushCintaNaturalStudentResult>(
      'push-cinta-natural-student',
      { studentId },
      'cinta-natural/students',
    );
  }

  /** Sincroniza aluna + projeto com o quadro Clint cadastrado. Normalmente disparado pela trigger. */
  syncProjectStudent(
    studentId: number,
    projectId: number,
    options?: { force?: boolean },
  ): Promise<ClintSyncProjectStudentResult> {
    return this.invoke<ClintSyncProjectStudentResult>(
      'sync-project-student',
      {
        studentId,
        projectId,
        force: options?.force === true,
      },
      'project-students/sync',
    );
  }
}

export const ClintService = new ClintServiceClass();
